import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import useAuthStore from './context/authStore';
import useClientServices from './hooks/useClientServices';

// Layouts
import PublicLayout from './components/layout/PublicLayout';

// Public pages
import HomePage from './pages/public/HomePage';
import ServicesPage from './pages/public/ServicesPage';
import ConsultingServicesPage from './pages/public/ConsultingServicesPage';
import TrainingServicesPage from './pages/public/TrainingServicesPage';
import StaffingServicesPage from './pages/public/StaffingServicesPage';
import StaffingJobSeekersPage from './pages/public/StaffingJobSeekersPage';
import AboutPage from './pages/public/AboutPage';
import ContactPage from './pages/public/ContactPage';
import IntakeFormPage from './pages/public/IntakeFormPage';
import ResponsibleAIPage from './pages/public/ResponsibleAIPage';
import PrivacyPage from './pages/public/PrivacyPage';
import ProposalPublicPage from './pages/public/ProposalPublicPage';
import PlacementOrderPublicPage from './pages/public/PlacementOrderPublicPage';
import AcceptInvitePage from './pages/public/AcceptInvitePage';
import EmailPreferencesPage from './pages/public/EmailPreferencesPage';
import EmailUnsubscribePage from './pages/public/EmailUnsubscribePage';
import InvoicePublicPage from './pages/public/InvoicePublicPage';
import SurveyPage from './pages/public/SurveyPage';

// Auth pages
import SplashPage from './pages/auth/SplashPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Dashboard pages
// (internal admin dashboard removed from public frontend for security)

// Client layout and home (client dashboard)
import ClientLayout from './components/layout/ClientLayout';

// Client dashboard
import DashboardPage from './pages/dashboard/DashboardPage';

// Project pages
import ProjectsListPage from './pages/projects/ProjectsListPage';
import ProjectDetailPage from './pages/projects/ProjectDetailPage';

// Document pages
import DocumentsPage from './pages/documents/DocumentsPage';
import DocumentDetailPage from './pages/documents/DocumentDetailPage';
import DocumentDraftReviewPage from './pages/documents/DocumentDraftReviewPage';
import SignaturesPage from './pages/signatures/SignaturesPage';
import SignDocumentPage from './pages/signatures/SignDocumentPage';

// Message pages
import MessagesPage from './pages/messages/MessagesPage';
import MessageThreadPage from './pages/messages/MessageThreadPage';

// LMS pages (public training)
import CourseCatalogPage from './pages/lms/CourseCatalogPage';
import CourseViewerPage from './pages/lms/CourseViewerPage';
import TrainingManagementPage from './pages/lms/TrainingManagementPage';
import LearningPathsPage from './pages/lms/LearningPathsPage';

// Certificates
import CertificatesPage from './pages/lms/CertificatesPage';
import CertificateDetailPage from './pages/lms/CertificateDetailPage';
import CourseDevPage from './pages/lms/CourseDevPage';

// Calendar
import CalendarPage from './pages/calendar/CalendarPage';

// Invoice pages
import InvoicesPage from './pages/invoices/InvoicesPage';
import InvoiceDetailPage from './pages/invoices/InvoiceDetailPage';
import PaymentMethodsPage from './pages/billing/PaymentMethodsPage';
// Settings
import SettingsPage from './pages/settings/SettingsPage';

// Staffing pages
import StaffingRequestsPage from './pages/staffing/StaffingRequestsPage';
import RateApprovalsPage from './pages/staffing/RateApprovalsPage';
import TimesheetApprovalsPage from './pages/staffing/TimesheetApprovalsPage';
import TeamMembersPage from './pages/team/TeamMembersPage';
import FacilitiesPage from './pages/organization/FacilitiesPage';
import { hasUserModuleAccess, canAccessBillingWorkspace, CLIENT_PORTAL_MODULES } from './utils/clientPortalPermissions';
import { normalizeRole } from './utils/roleNormalization';

// Protected route wrapper
const ProtectedRoute = ({ children, allowedRoles, moduleKey, requiredLevel = 'view' }) => {
  const { isAuthenticated, user, isLoading } = useAuthStore();
  const effectiveUserRole = normalizeRole(user?.role);
  const normalizedAllowedRoles = allowedRoles
    ? Array.from(new Set([
        ...allowedRoles,
        ...(allowedRoles.includes('facility_admin') ? ['org_admin'] : []),
        ...(allowedRoles.includes('facility_manager') ? ['org_team'] : []),
      ]))
    : null;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/portal" replace />;
  }

  if (normalizedAllowedRoles && !normalizedAllowedRoles.includes(effectiveUserRole)) {
    // If the user is an internal team member, send them to the standalone admin portal for security
    if (['admin', 'stratel_admin', 'consultant', 'consultant_lead'].includes(effectiveUserRole)) {
      const adminUrl = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';
      if (typeof window !== 'undefined') {
        window.location.assign(adminUrl);
        return null;
      }
    }

    // Otherwise redirect to the client dashboard (safer than the public home page)
    return <Navigate to="/dashboard" replace />;
  }

  if (moduleKey && !hasUserModuleAccess(user, moduleKey, requiredLevel)) {
    if (moduleKey === CLIENT_PORTAL_MODULES.billing && canAccessBillingWorkspace(user)) {
      return children;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Redirect if authenticated
const PublicOnlyRoute = ({ children }) => {
  const { isAuthenticated } = useAuthStore();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const ServiceEnabledRoute = ({ children, serviceKey }) => {
  const { user } = useAuthStore();
  const { services, loading } = useClientServices();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (serviceKey === 'lms' && user?.role === 'worker') {
    return children;
  }

  if (!services?.[serviceKey]?.enabled) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AdminRedirect() {
  const adminUrl = import.meta.env.VITE_ADMIN_URL || 'http://localhost:5174';
  if (typeof window !== 'undefined') {
    window.location.assign(adminUrl);
    return null;
  }
  return null;
}

function App() {
  const { fetchUser, isAuthenticated } = useAuthStore();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token && !isAuthenticated) {
      fetchUser();
    }
  }, []);

  return (
    <Routes>
      {/* Public routes */}
      <Route element={<PublicLayout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/services/consulting" element={<ConsultingServicesPage />} />
        <Route path="/services/training" element={<TrainingServicesPage />} />
        <Route path="/services/staffing" element={<StaffingServicesPage />} />
        <Route path="/services/staffing/job-seekers" element={<StaffingJobSeekersPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
      </Route>

      {/* Standalone public pages */}
            <Route path="/get-started" element={<IntakeFormPage />} />
            <Route path="/responsible-ai" element={<ResponsibleAIPage />} />
            <Route path="/p/:token" element={<ProposalPublicPage />} />
            <Route path="/m/:token" element={<ProposalPublicPage />} />
            <Route path="/po/:token" element={<PlacementOrderPublicPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/preferences/:token" element={<EmailPreferencesPage />} />
            <Route path="/unsubscribe/:token" element={<EmailUnsubscribePage />} />
            <Route path="/i/:token" element={<InvoicePublicPage />} />
            <Route path="/survey/:token" element={<SurveyPage />} />

      {/* Client portal splash */}
      <Route
        path="/portal"
        element={
          <PublicOnlyRoute>
            <SplashPage />
          </PublicOnlyRoute>
        }
      />

      {/* Auth routes */}
      <Route
        path="/login"
        element={
          <PublicOnlyRoute>
            <LoginPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicOnlyRoute>
            <ForgotPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <PublicOnlyRoute>
            <ResetPasswordPage />
          </PublicOnlyRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicOnlyRoute>
            <RegisterPage />
          </PublicOnlyRoute>
        }
      />

      {/* Client protected routes (clients only) */}
      <Route
        element={
          <ProtectedRoute allowedRoles={['org_team', 'facility_admin', 'facility_manager', 'facility_worker', 'worker']}>
            <ClientLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/projects" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.consulting_workspace} requiredLevel="view"><ServiceEnabledRoute serviceKey="consulting"><ProjectsListPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/projects/:id" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.consulting_workspace} requiredLevel="view"><ServiceEnabledRoute serviceKey="consulting"><ProjectDetailPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/projects/:id/documents" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.consulting_workspace} requiredLevel="view"><ServiceEnabledRoute serviceKey="consulting"><DocumentsPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/projects/:id/messages" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager","facility_worker"]}><MessagesPage /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.consulting_workspace} requiredLevel="view"><ServiceEnabledRoute serviceKey="consulting"><DocumentsPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/documents/draft/:id" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.consulting_workspace} requiredLevel="view"><ServiceEnabledRoute serviceKey="consulting"><DocumentDraftReviewPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/documents/:id/sign" element={<SignDocumentPage />} />
        <Route path="/documents/:id" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.consulting_workspace} requiredLevel="view"><ServiceEnabledRoute serviceKey="consulting"><DocumentDetailPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/signatures" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.consulting_workspace} requiredLevel="view"><ServiceEnabledRoute serviceKey="consulting"><SignaturesPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/messages" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager","facility_worker"]}><MessagesPage /></ProtectedRoute>} />
        <Route path="/messages/:id" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager","facility_worker"]}><MessageThreadPage /></ProtectedRoute>} />
        <Route path="/invoices" element={<ProtectedRoute allowedRoles={["org_admin","facility_admin"]} moduleKey={CLIENT_PORTAL_MODULES.billing} requiredLevel="view"><InvoicesPage /></ProtectedRoute>} />
        <Route path="/invoices/:id" element={<ProtectedRoute allowedRoles={["org_admin","facility_admin"]} moduleKey={CLIENT_PORTAL_MODULES.billing} requiredLevel="view"><InvoiceDetailPage /></ProtectedRoute>} />
        <Route path="/billing/payment-methods" element={<ProtectedRoute allowedRoles={["org_admin","facility_admin"]} moduleKey={CLIENT_PORTAL_MODULES.billing} requiredLevel="admin"><PaymentMethodsPage /></ProtectedRoute>} />
        <Route path="/team" element={<ProtectedRoute allowedRoles={["org_admin","org_team","facility_admin","facility_manager","facility_worker"]} moduleKey={CLIENT_PORTAL_MODULES.team_management} requiredLevel="view"><TeamMembersPage /></ProtectedRoute>} />
        <Route path="/facilities" element={<ProtectedRoute allowedRoles={["org_admin"]} moduleKey={CLIENT_PORTAL_MODULES.organization_settings} requiredLevel="view"><FacilitiesPage /></ProtectedRoute>} />

        {/* Public LMS (Training) */}
        <Route path="/training" element={<ServiceEnabledRoute serviceKey="lms"><CourseCatalogPage /></ServiceEnabledRoute>} />
        <Route path="/learning-paths" element={<ServiceEnabledRoute serviceKey="lms"><LearningPathsPage /></ServiceEnabledRoute>} />
        <Route path="/training/:courseId" element={<ServiceEnabledRoute serviceKey="lms"><CourseViewerPage /></ServiceEnabledRoute>} />
        <Route path="/training/:courseId/lesson/:lessonId" element={<ServiceEnabledRoute serviceKey="lms"><CourseViewerPage /></ServiceEnabledRoute>} />
        <Route path="/training-management" element={<ProtectedRoute allowedRoles={["org_admin","org_team","facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.lms_management} requiredLevel="view"><ServiceEnabledRoute serviceKey="lms"><TrainingManagementPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/lms/course-dev" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.lms_management} requiredLevel="view"><ServiceEnabledRoute serviceKey="lms"><CourseDevPage /></ServiceEnabledRoute></ProtectedRoute>} />

        {/* Certificates */}
        <Route path="/certificates" element={<ServiceEnabledRoute serviceKey="lms"><CertificatesPage /></ServiceEnabledRoute>} />
        <Route path="/certificates/:id" element={<ServiceEnabledRoute serviceKey="lms"><CertificateDetailPage /></ServiceEnabledRoute>} />

        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/staffing/requests" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.staffing_requests} requiredLevel="view"><ServiceEnabledRoute serviceKey="staffing"><StaffingRequestsPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/staffing/rate-approvals" element={<ProtectedRoute allowedRoles={["facility_admin"]} moduleKey={CLIENT_PORTAL_MODULES.staffing_rate_approvals} requiredLevel="view"><ServiceEnabledRoute serviceKey="staffing"><RateApprovalsPage /></ServiceEnabledRoute></ProtectedRoute>} />
        <Route path="/staffing/timesheet-approvals" element={<ProtectedRoute allowedRoles={["facility_admin","facility_manager"]} moduleKey={CLIENT_PORTAL_MODULES.staffing_timesheet_approvals} requiredLevel="view"><ServiceEnabledRoute serviceKey="staffing"><TimesheetApprovalsPage /></ServiceEnabledRoute></ProtectedRoute>} />
      </Route>

      {/* Redirect any admin-only paths to the standalone admin portal */}
      <Route path="/organizations/*" element={<AdminRedirect />} />
      <Route path="/users/*" element={<AdminRedirect />} />
      <Route path="/intake/*" element={<AdminRedirect />} />
      <Route path="/campaigns/*" element={<AdminRedirect />} />
      <Route path="/drip/*" element={<AdminRedirect />} />
      <Route path="/leads/*" element={<AdminRedirect />} />
      <Route path="/ltc-leads/*" element={<AdminRedirect />} />
      <Route path="/templates/*" element={<AdminRedirect />} />
      <Route path="/analytics/*" element={<AdminRedirect />} />
      <Route path="/compliance/*" element={<AdminRedirect />} />
      <Route path="/interventions/*" element={<AdminRedirect />} />
      <Route path="/calendar/*" element={<AdminRedirect />} />
      <Route path="/booking-links/*" element={<AdminRedirect />} />
      <Route path="/staffing/*" element={<AdminRedirect />} />

      {/* Catch all - 404 */}

      {/* Catch all - 404 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
